/**
 * Análise de Atividades — telemetria e-commerce / ERP.
 */

import { useCallback, useEffect, useState } from 'react'
import { Activity, Eye, ShoppingCart, CreditCard, Users, Loader2 } from 'lucide-react'
import { telemetryService } from '../../../services/telemetryService'
import './AtividadesPage.css'

const PERIODS = [
  { id: 'today', label: 'Hoje' },
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
]

function formatNum(n) {
  return new Intl.NumberFormat('pt-BR').format(Number(n) || 0)
}

function formatMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(n))
}

function formatSeconds(s) {
  if (s == null || Number.isNaN(Number(s))) return '—'
  const n = Number(s)
  if (n < 60) return `${Math.round(n)}s`
  const m = Math.floor(n / 60)
  const r = Math.round(n % 60)
  return `${m}m ${r}s`
}

export default function AtividadesPage() {
  const [period, setPeriod] = useState('7d')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const load = useCallback(async (p) => {
    setLoading(true)
    setError(null)
    try {
      const res = await telemetryService.getAnalytics(p)
      if (res?.success) {
        setData(res.data)
      } else {
        setError(res?.message || 'Falha ao carregar métricas')
        setData(null)
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Erro ao carregar')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(period)
  }, [period, load])

  const funnel = data?.funnel
  const active = data?.activeUsers

  return (
    <div className="atividades-page">
      <header className="atividades-header">
        <div>
          <p className="atividades-kicker">Dashboard</p>
          <h1>Análise de Atividades</h1>
          <p className="atividades-sub">
            Funil da loja, produtos no carrinho e páginas do e-commerce (sem rotas /erp).
          </p>
        </div>
        <div className="atividades-periods" role="group" aria-label="Período">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`atividades-period${period === p.id ? ' is-on' : ''}`}
              onClick={() => setPeriod(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      {loading && (
        <div className="atividades-loading">
          <Loader2 className="atividades-spin" size={28} />
          <span>Carregando métricas…</span>
        </div>
      )}

      {!loading && error && (
        <div className="atividades-error" role="alert">
          {error}
        </div>
      )}

      {!loading && data && (
        <>
          <section className="atividades-kpis">
            <article className="atividades-kpi">
              <Eye size={18} aria-hidden />
              <div>
                <span>Views de produto</span>
                <strong>{formatNum(funnel?.view_product)}</strong>
              </div>
            </article>
            <article className="atividades-kpi">
              <ShoppingCart size={18} aria-hidden />
              <div>
                <span>Add ao carrinho</span>
                <strong>{formatNum(funnel?.add_to_cart)}</strong>
              </div>
            </article>
            <article className="atividades-kpi">
              <CreditCard size={18} aria-hidden />
              <div>
                <span>Checkout</span>
                <strong>{formatNum(funnel?.checkout_start)}</strong>
              </div>
            </article>
            <article className="atividades-kpi">
              <Activity size={18} aria-hidden />
              <div>
                <span>Compras</span>
                <strong>{formatNum(funnel?.purchase)}</strong>
              </div>
            </article>
          </section>

          <section className="atividades-grid">
            <div className="atividades-card">
              <h2>Funil de conversão</h2>
              <ul className="atividades-funnel">
                <li>
                  <span>Produto → Carrinho</span>
                  <strong>{funnel?.rates?.view_to_cart ?? 0}%</strong>
                </li>
                <li>
                  <span>Carrinho → Checkout</span>
                  <strong>{funnel?.rates?.cart_to_checkout ?? 0}%</strong>
                </li>
                <li>
                  <span>Checkout → Compra</span>
                  <strong>{funnel?.rates?.checkout_to_purchase ?? 0}%</strong>
                </li>
                <li>
                  <span>Produto → Compra</span>
                  <strong>{funnel?.rates?.view_to_purchase ?? 0}%</strong>
                </li>
              </ul>
            </div>

            <div className="atividades-card">
              <h2>
                <Users size={18} aria-hidden /> Visitantes
              </h2>
              <ul className="atividades-funnel">
                <li>
                  <span>Anônimos</span>
                  <strong>{formatNum(active?.anonymousVisitors)}</strong>
                </li>
                <li>
                  <span>Autenticados (sessões)</span>
                  <strong>{formatNum(active?.authenticatedVisitors)}</strong>
                </li>
                <li>
                  <span>Usuários únicos logados</span>
                  <strong>{formatNum(active?.authenticatedUsers)}</strong>
                </li>
              </ul>
            </div>
          </section>

          <section className="atividades-grid">
            <div className="atividades-card atividades-card--wide">
              <h2>Produtos adicionados ao carrinho</h2>
              <p className="atividades-card-hint">
                Ranking por unidades adicionadas no período (eventos add_to_cart).
              </p>
              <div className="atividades-table-wrap">
                <table className="atividades-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Unidades</th>
                      <th>Adições</th>
                      <th>Visitantes</th>
                      <th>Preço médio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.topCartProducts || []).length === 0 ? (
                      <tr>
                        <td colSpan={5}>Nenhum produto adicionado ao carrinho no período.</td>
                      </tr>
                    ) : (
                      data.topCartProducts.map((row) => (
                        <tr key={`cart-${row.productId}`}>
                          <td>
                            <div className="atividades-prod-name">{row.productName}</div>
                            <div className="atividades-prod-id">
                              #{row.productId}
                              {row.category ? ` · ${row.category}` : ''}
                            </div>
                          </td>
                          <td>{formatNum(row.unitsAdded)}</td>
                          <td>{formatNum(row.addEvents)}</td>
                          <td>{formatNum(row.uniqueShoppers)}</td>
                          <td>{formatMoney(row.avgUnitPrice)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="atividades-card atividades-card--wide">
              <h2>Produtos mais vistos</h2>
              <div className="atividades-table-wrap">
                <table className="atividades-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Views</th>
                      <th>Carrinho</th>
                      <th>Compras</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.topProducts || []).length === 0 ? (
                      <tr>
                        <td colSpan={4}>Sem dados no período.</td>
                      </tr>
                    ) : (
                      data.topProducts.map((row) => (
                        <tr key={row.productId}>
                          <td>
                            <div className="atividades-prod-name">{row.productName}</div>
                            <div className="atividades-prod-id">#{row.productId}</div>
                          </td>
                          <td>{formatNum(row.views)}</td>
                          <td>{formatNum(row.addToCart)}</td>
                          <td>{formatNum(row.purchases)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="atividades-card atividades-card--wide">
              <h2>Páginas mais acessadas</h2>
              <div className="atividades-table-wrap">
                <table className="atividades-table">
                  <thead>
                    <tr>
                      <th>URL</th>
                      <th>Acessos</th>
                      <th>Tempo médio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.topPages || []).length === 0 ? (
                      <tr>
                        <td colSpan={3}>Sem dados no período.</td>
                      </tr>
                    ) : (
                      data.topPages.map((row) => (
                        <tr key={row.pageUrl}>
                          <td className="atividades-url">{row.pageUrl}</td>
                          <td>{formatNum(row.views)}</td>
                          <td>{formatSeconds(row.avgTimeSpentSeconds)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  )
}
